import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Calculator, 
  TrendingUp, 
  PiggyBank, 
  Home, 
  Euro, 
  CheckCircle2,
  ArrowRight,
  BarChart3,
  FileText,
  Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function SimulateurRentabiliteLocative() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Helmet>
        <title>Simulateur Rentabilité Locative Gratuit 2025 | Achat Immo Gagnant</title>
        <meta name="description" content="Calculez gratuitement la rentabilité de votre investissement locatif. Simulateur complet avec calcul du cash-flow, rendement net, comparaison Airbnb vs location traditionnelle." />
        <meta name="keywords" content="simulateur rentabilité locative, calcul rentabilité locative, simulateur investissement immobilier, rentabilité locative gratuit" />
        <link rel="canonical" href="https://achat-immo-gagnant.fr/guides/simulateur-rentabilite-locative" />
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Home className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-800">Achat Immo Gagnant</span>
          </Link>
          <Link to="/">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              Accéder au simulateur
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Simulateur de Rentabilité Locative
          </h1>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Calculez précisément la rentabilité de votre investissement immobilier locatif. 
            Comparez location traditionnelle et Airbnb en quelques clics.
          </p>
          <Link to="/">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6">
              <Calculator className="mr-2 h-5 w-5" />
              Lancer une simulation gratuite
            </Button>
          </Link>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        
        {/* What is it */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">
            Qu'est-ce qu'un simulateur de rentabilité locative ?
          </h2>
          <div className="prose prose-lg text-slate-600">
            <p>
              Un <strong>simulateur de rentabilité locative</strong> est un outil de calcul qui permet aux investisseurs immobiliers d'évaluer précisément le rendement potentiel d'un bien avant son acquisition. Contrairement à un simple calcul de rendement brut, notre simulateur prend en compte l'ensemble des paramètres financiers pour vous donner une vision réaliste de votre investissement.
            </p>
            <p>
              Notre outil va bien au-delà d'un simple <em>simulateur rentabilité locative excel</em> : il intègre des données de marché en temps réel (prix Airbnb, loyers moyens par zone) et génère un dossier bancaire complet pour faciliter l'obtention de votre financement.
            </p>
          </div>
        </section>

        {/* Key Features */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-8">
            Ce que calcule notre simulateur
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Euro className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Rentabilité brute</h3>
              <p className="text-slate-600">
                Le ratio entre les loyers annuels et le prix d'achat. Un premier indicateur rapide mais incomplet.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                <strong>Formule :</strong> (Loyers annuels / Prix d'achat) × 100
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Rentabilité nette</h3>
              <p className="text-slate-600">
                Intègre toutes les charges : taxe foncière, assurance, gestion, travaux, vacance locative.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                <strong>Formule :</strong> (Loyers - Charges) / Investissement total × 100
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <PiggyBank className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Cash-flow mensuel</h3>
              <p className="text-slate-600">
                L'argent qui reste (ou manque) chaque mois après paiement du crédit et des charges. L'indicateur clé !
              </p>
              <p className="text-sm text-slate-500 mt-2">
                <strong>Formule :</strong> Loyers - Charges - Mensualité crédit
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Solde de l'opération</h3>
              <p className="text-slate-600">
                Ce que vous récupérez en cas de revente : prix de vente - reste du prêt + cash-flow cumulé.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                <strong>Horizon :</strong> Projection sur 5, 10, 15 ou 20 ans
              </p>
            </div>
          </div>
        </section>

        {/* Why use our simulator */}
        <section className="mb-16 bg-blue-50 rounded-2xl p-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">
            Pourquoi utiliser notre simulateur ?
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-800">Données de marché en temps réel</h3>
                <p className="text-slate-600">Prix Airbnb scrapés en direct, loyers moyens par zone (données INSEE 2025)</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-800">Comparaison Airbnb vs Location longue durée</h3>
                <p className="text-slate-600">Simulez les deux scénarios et comparez les rentabilités nettes</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-800">Calcul avec emprunt intégré</h3>
                <p className="text-slate-600">Mensualités, assurance, durée du prêt : tout est pris en compte</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-800">Dossier PDF pour votre banquier</h3>
                <p className="text-slate-600">Générez un argumentaire professionnel pour défendre votre projet</p>
              </div>
            </div>
          </div>
        </section>

        {/* How to calculate */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">
            Comment calculer la rentabilité locative ?
          </h2>
          <div className="prose prose-lg text-slate-600">
            <p>
              Le calcul de la <strong>rentabilité locative</strong> se fait en plusieurs étapes. Voici la méthode utilisée par notre simulateur :
            </p>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">1. Calculer l'investissement total</h3>
            <p>
              L'investissement total comprend : le prix d'achat + les frais de notaire (~8% dans l'ancien) + les travaux éventuels + les frais d'agence.
            </p>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">2. Estimer les revenus locatifs</h3>
            <p>
              Pour la location traditionnelle, utilisez les loyers moyens de la zone. Pour Airbnb, estimez le prix par nuit × le nombre de nuits louées (taux d'occupation réaliste : 60-70%).
            </p>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">3. Déduire toutes les charges</h3>
            <ul>
              <li>Taxe foncière (environ 1-1,5% du prix du bien)</li>
              <li>Assurance PNO (Propriétaire Non Occupant)</li>
              <li>Charges de copropriété non récupérables</li>
              <li>Provision pour travaux et vacance locative</li>
              <li>Frais de gestion si délégation</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">4. Intégrer le financement</h3>
            <p>
              Si vous empruntez (ce qui est recommandé pour bénéficier de l'effet de levier), calculez votre mensualité de crédit. Le <strong>cash-flow</strong> = Loyers - Charges - Mensualité.
            </p>
          </div>
        </section>

        {/* What is a good yield */}
        <section className="mb-16 bg-green-50 rounded-2xl p-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">
            Qu'est-ce qu'une bonne rentabilité locative ?
          </h2>
          <div className="prose prose-lg text-slate-600">
            <p>
              La notion de "bonne rentabilité" dépend de votre stratégie et de votre zone géographique :
            </p>
            <div className="grid md:grid-cols-3 gap-4 my-6">
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <div className="text-3xl font-bold text-red-600 mb-2">&lt; 4%</div>
                <div className="font-semibold text-slate-800">Rentabilité faible</div>
                <p className="text-sm text-slate-600">Typique des grandes villes (Paris, Lyon). Miser sur la plus-value.</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <div className="text-3xl font-bold text-amber-600 mb-2">4-7%</div>
                <div className="font-semibold text-slate-800">Rentabilité correcte</div>
                <p className="text-sm text-slate-600">Villes moyennes. Bon équilibre rendement/risque.</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <div className="text-3xl font-bold text-green-600 mb-2">&gt; 7%</div>
                <div className="font-semibold text-slate-800">Haute rentabilité</div>
                <p className="text-sm text-slate-600">Petites villes, niches. Attention à la liquidité.</p>
              </div>
            </div>
            <p>
              <strong>Notre conseil :</strong> Ne regardez pas que le rendement brut. Un bien avec 10% de rendement brut mais un cash-flow négatif reste un mauvais investissement. Notre simulateur calcule le <strong>cash-flow réel</strong> pour éviter les mauvaises surprises.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Prêt à calculer votre rentabilité ?
          </h2>
          <p className="text-blue-100 mb-6 max-w-xl mx-auto">
            Notre simulateur vous guide étape par étape et génère un dossier complet pour votre banquier.
          </p>
          <Link to="/">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8">
              Simuler mon investissement
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <p className="text-blue-200 text-sm mt-4">Simulation gratuite • Dossier PDF à 4,99€</p>
        </section>

        {/* Related links for SEO */}
        <section className="mt-16">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">Guides connexes</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/guides/calcul-rentabilite-locative-emprunt" className="bg-white rounded-lg p-4 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-semibold text-blue-600">Calcul rentabilité locative avec emprunt</h3>
              <p className="text-sm text-slate-600">Comment intégrer le crédit dans votre calcul de rendement</p>
            </Link>
            <Link to="/guides/rentabilite-airbnb-vs-location" className="bg-white rounded-lg p-4 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-semibold text-blue-600">Airbnb vs Location traditionnelle</h3>
              <p className="text-sm text-slate-600">Quelle stratégie est la plus rentable pour votre bien ?</p>
            </Link>
            <Link to="/guides/cash-flow-immobilier" className="bg-white rounded-lg p-4 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-semibold text-blue-600">Comprendre le cash-flow immobilier</h3>
              <p className="text-sm text-slate-600">L'indicateur clé pour un investissement qui s'autofinance</p>
            </Link>
            <Link to="/guides/fiscalite-location-meublee" className="bg-white rounded-lg p-4 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-semibold text-blue-600">Fiscalité de la location meublée (LMNP)</h3>
              <p className="text-sm text-slate-600">Optimisez vos impôts avec le statut LMNP</p>
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="font-bold text-lg mb-4">Achat Immo Gagnant</h3>
              <p className="text-slate-400 text-sm">
                Le simulateur de rentabilité locative qui vous aide à prendre les bonnes décisions d'investissement.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Simulateur</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/" className="hover:text-white">Lancer une simulation</Link></li>
                <li><Link to="/guides/simulateur-rentabilite-locative" className="hover:text-white">Comment ça marche</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Guides</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/guides/calcul-rentabilite-locative-emprunt" className="hover:text-white">Calcul avec emprunt</Link></li>
                <li><Link to="/guides/rentabilite-airbnb-vs-location" className="hover:text-white">Airbnb vs Location</Link></li>
                <li><Link to="/guides/cash-flow-immobilier" className="hover:text-white">Cash-flow immobilier</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Légal</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><Link to="/cgv" className="hover:text-white">CGV</Link></li>
                <li><Link to="/cgu" className="hover:text-white">CGU</Link></li>
                <li><Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-500 text-sm">
            © 2025 Achat Immo Gagnant. Tous droits réservés.
          </div>
        </div>
      </footer>
    </div>
  );
}
